<template>
  <div class="salon-app salon-bookings">
    <header class="salon-topbar">
      <div class="salon-topbar-row">
        <button class="salon-back" type="button" :aria-label="$t('salon.common.goBack')" @click="goBack">
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <h1 class="salon-topbar-title">{{ $t("salon.bookings.title") }}</h1>
        <SalonProfileMenu
          :initials="memberInitials"
          :name="memberName"
          @navigate="$emit('profile', $event)"
        />
      </div>
    </header>

    <main class="salon-main">
      <template v-if="!isLoading && (upcoming.length || past.length)">
        <section v-if="upcoming.length" class="salon-group">
          <h2 class="salon-group-title">{{ $t("salon.bookings.upcoming") }}</h2>
          <button
            v-for="booking in upcoming"
            :key="booking.booking_code"
            class="salon-booking"
            type="button"
            @click="$emit('open', booking)"
          >
            <span class="salon-booking-rail" aria-hidden="true"></span>
            <span class="salon-booking-body">
              <span class="salon-booking-when">
                {{ dateOf(booking) }} · {{ timeOf(booking) }}
              </span>
              <span class="salon-booking-service">{{ booking.service_summary }}</span>
              <span class="salon-booking-where">
                {{ booking.branch_name }} · {{ staffOf(booking) }}
              </span>
            </span>
            <span class="salon-booking-tail">
              <span class="salon-badge salon-badge--live">{{ statusOf(booking) }}</span>
              <span v-if="showPrice" class="salon-booking-total">{{ formatPrice(booking.total_amount) }}</span>
            </span>
          </button>
        </section>

        <section v-if="past.length" class="salon-group">
          <h2 class="salon-group-title">{{ $t("salon.bookings.past") }}</h2>
          <!-- A div rather than a button, unlike the Upcoming rows above: SH017 puts a real
               "Vote for stylist" button inside this card, and a button nested in a button is
               invalid markup that browsers resolve by dropping one of them. -->
          <div
            v-for="booking in past"
            :key="booking.booking_code"
            class="salon-booking salon-booking--past"
            role="button"
            tabindex="0"
            @click="$emit('open', booking)"
            @keydown.enter.prevent="$emit('open', booking)"
            @keydown.space.prevent="$emit('open', booking)"
          >
            <span class="salon-booking-rail" aria-hidden="true"></span>
            <span class="salon-booking-main">
              <span class="salon-booking-head">
                <span class="salon-booking-body">
                  <span class="salon-booking-when">
                    {{ dateOf(booking) }} · {{ timeOf(booking) }}
                  </span>
                  <span class="salon-booking-service">{{ booking.service_summary }}</span>
                  <span class="salon-booking-where">
                    {{ booking.branch_name }} · {{ staffOf(booking) }}
                  </span>
                </span>
                <span class="salon-booking-tail">
                  <span
                    class="salon-badge"
                    :class="{ 'salon-badge--off': booking.status === cancelledStatus }"
                  >
                    {{ statusOf(booking) }}
                  </span>
                  <span v-if="showPrice" class="salon-booking-total">{{ formatPrice(booking.total_amount) }}</span>
                </span>
              </span>

              <!-- SH017 — the invitation to rate. The API decides whether it appears: the
                   visit has to have been paid for, not merely to have passed. -->
              <button
                v-if="booking.can_rate"
                class="salon-vote"
                type="button"
                @click.stop="$emit('rate', booking)"
              >
                <svg class="salon-vote-star" viewBox="0 0 24 24" width="18" height="18"
                  aria-hidden="true">
                  <path d="M12 3l2.6 5.6 6.1.8-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6L3.3 9.4l6.1-.8L12 3z"
                    fill="currentColor" />
                </svg>
                <span class="salon-vote-text">{{ $t("salon.bookings.voteStylist") }}</span>
                <svg class="salon-vote-chevron" viewBox="0 0 24 24" width="16" height="16"
                  aria-hidden="true">
                  <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>

              <!-- Already voted: the score they gave, read-only. One vote per appointment. -->
              <span v-else-if="booking.my_rating" class="salon-rated">
                <svg
                  v-for="star in 5"
                  :key="star"
                  class="salon-rated-star"
                  :class="{ 'salon-rated-star--on': star <= booking.my_rating.rating }"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  aria-hidden="true"
                >
                  <path d="M12 3l2.6 5.6 6.1.8-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6L3.3 9.4l6.1-.8L12 3z"
                    fill="currentColor" />
                </svg>
                <span class="salon-rated-text">{{ $t("salon.bookings.rated") }}</span>
              </span>
            </span>
          </div>
        </section>
      </template>

      <p v-else-if="!isLoading && loadFailed" class="salon-empty salon-empty--error">
        {{ $t("salon.bookings.loadError") }}
        <button class="salon-retry" type="button" @click="getBookings">{{ $t("salon.bookings.retry") }}</button>
      </p>

      <div v-else-if="!isLoading" class="salon-blank">
        <h2 class="salon-blank-title">{{ $t("salon.bookings.emptyTitle") }}</h2>
        <p class="salon-blank-text">
          {{ $t("salon.bookings.emptyText") }}
        </p>
      </div>
    </main>

    <nav class="salon-cta">
      <button class="salon-cta-btn" type="button" @click="$emit('book-another')">
        {{ $t("salon.bookings.bookCta") }}
      </button>
    </nav>

    <material-loading :active="isLoading" />
  </div>
</template>

<script>
import MaterialLoading from "@/components/MaterialLoading.vue";
import SalonBookingService from "@/services/salonBooking.service";
import { SALON_BOOKING_CANCELLED } from "@/config/const";
import SalonProfileMenu from "@/salon/components/ProfileMenu.vue";
import "@/salon/styles/tokens.css";

// SH046 — Chinese renders dates as "2026年9月14日 周一". Every other locale keeps the English text.
const ZH_WEEKDAYS = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];

// SH046 — the API's status_text, keyed to the label shown in the current language.
const STATUS_KEYS = {
  Confirmed: "salon.bookings.status.confirmed",
  Completed: "salon.bookings.status.completed",
  Cancelled: "salon.bookings.status.cancelled",
};

/**
 * SH010 — "View my bookings".
 *
 * The API splits the two lists itself (upcoming soonest-first, history newest-first), so
 * nothing here re-decides what counts as upcoming: "has it started yet" depends on the
 * salon's clock, not the phone's.
 */
export default {
  name: "SalonMyBookings",
  components: {
    SalonProfileMenu,
    MaterialLoading,
  },
  props: {
    // SH016 — the avatar in the top bar, and who it stands for.
    memberInitials: {
      type: String,
      default: "",
    },
    memberName: {
      type: String,
      default: "",
    },
    // SH032 — minty_branches.salon_show_service_price, carried down from the outlet.
    // False keeps every amount off this screen; the booking still holds them and the
    // counter still bills against the booking reference.
    showPrice: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["back", "open", "rate", "book-another", "profile"],
  data() {
    return {
      isLoading: false,
      loadFailed: false,
      upcoming: [],
      past: [],
      cancelledStatus: SALON_BOOKING_CANCELLED,
    };
  },
  computed: {
    /**
     * SH020 — the appointment the Back arrow opens.
     *
     * The upcoming one comes first: the API sorts it soonest-first, and a booking that has
     * not happened yet is the one the customer means by "my booking". Only once there is
     * none does history stand in, newest-first as the API returns it.
     */
    latestBooking() {
      return this.upcoming[0] || this.past[0] || null;
    },
  },
  created() {
    this.getBookings();
  },
  methods: {
    /**
     * SH020 — Back opens the latest appointment's ticket rather than the screen behind
     * this one. With nothing booked there is no ticket to open, so it falls back to where
     * the customer came from instead of becoming a dead button.
     */
    goBack() {
      if (this.latestBooking) {
        this.$emit("open", this.latestBooking);
        return;
      }

      this.$emit("back");
    },

    formatPrice(value) {
      return "$" + Number(value || 0).toFixed(2);
    },

    // SH046 — date_text / time_text / status_text / staff_text come from the API in English.
    // English shows them untouched; Chinese rebuilds the date and time from date / time.
    dateOf(booking) {
      const [year, month, day] = String(booking.date || "").split("-").map(Number);

      if (this.$i18n.locale !== "zh" || !year || !month || !day) {
        return booking.date_text;
      }

      return year + "年" + month + "月" + day + "日 " + ZH_WEEKDAYS[new Date(year, month - 1, day).getDay()];
    },

    timeOf(booking) {
      const [hour, minute] = String(booking.time || "").split(":").map(Number);

      if (this.$i18n.locale !== "zh" || Number.isNaN(hour) || Number.isNaN(minute) || !booking.time) {
        return booking.time_text;
      }

      return (hour >= 12 ? "下午" : "上午") + (hour % 12 === 0 ? 12 : hour % 12) + ":" + String(minute).padStart(2, "0");
    },

    statusOf(booking) {
      const key = STATUS_KEYS[booking.status_text];

      return key ? this.$t(key) : booking.status_text;
    },

    staffOf(booking) {
      return booking.staff_text === "To be assigned"
        ? this.$t("salon.ticket.toBeAssigned")
        : booking.staff_text;
    },

    async getBookings() {
      this.isLoading = true;
      this.loadFailed = false;

      try {
        const response = await SalonBookingService.myBookings();

        if (response.data.result.isSuccess) {
          this.upcoming = response.data.data.upcoming ?? [];
          this.past = response.data.data.past ?? [];
        } else {
          this.upcoming = [];
          this.past = [];
          this.loadFailed = true;
        }
      } catch (error) {
        // A member with no history and an unreachable API both render as an empty list;
        // only one of them deserves a Retry.
        this.upcoming = [];
        this.past = [];
        this.loadFailed = true;
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Tokens come from src/salon/styles/tokens.css, namespaced under .salon-app. */
.salon-bookings {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  max-width: var(--salon-max-width);
  margin: 0 auto;
  background: var(--salon-background);
  color: var(--salon-on-background);
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
}

/* ── Top app bar ───────────────────────────────────────────────── */
.salon-topbar {
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  padding: 16px var(--salon-container-padding) 12px;
  background: var(--salon-surface);
}

.salon-topbar-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
}

.salon-back {
  position: absolute;
  left: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  color: var(--salon-primary);
  background: transparent;
  border: 0;
  cursor: pointer;
}

.salon-back:active {
  transform: scale(0.95);
}

/* material-dashboard forces Roboto Slab onto every h1-h3 globally, and that beats a scoped
   rule that simply omits font-family. Every heading in this file therefore names its own
   face — see the MTH027 PaymentSuccess reskin for the same trap. */
.salon-topbar-title {
  margin: 0;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: var(--salon-primary);
}

/* ── Canvas ────────────────────────────────────────────────────── */
.salon-main {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--salon-stack-lg);
  padding: var(--salon-stack-md) var(--salon-container-padding) 140px;
}

.salon-group {
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-sm);
}

.salon-group-title {
  margin: 0 0 4px;
  font-family: var(--salon-font-headline);
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: var(--salon-on-surface);
}

/* ── Booking cards ─────────────────────────────────────────────── */
.salon-booking {
  display: flex;
  gap: 14px;
  align-items: stretch;
  width: 100%;
  padding: 16px;
  font-family: var(--salon-font-body);
  text-align: left;
  background: var(--salon-surface-lowest);
  border: 0;
  border-radius: 12px;
  box-shadow: var(--salon-shadow-soft);
  cursor: pointer;
  transition: transform 0.2s ease;
}

.salon-booking:active {
  transform: scale(0.99);
}

/* The dimming that marks a past booking sits on its content, not on the card: SH017's gold
   "Vote for stylist" is a live call to action inside a finished appointment, and a parent
   opacity would wash it out with no way to opt back in. */
.salon-booking--past .salon-booking-head,
.salon-booking--past .salon-booking-rail {
  opacity: 0.7;
}

.salon-booking[role="button"] {
  /* A div does not inherit the button reset above; only the parts that differ. */
  box-sizing: border-box;
}

.salon-booking[role="button"]:focus-visible {
  outline: 2px solid var(--salon-primary);
  outline-offset: 2px;
}

/* Body and tail side by side, with the vote strip stacked beneath them. */
.salon-booking-main {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.salon-booking-head {
  display: flex;
  gap: 14px;
  align-items: stretch;
  min-width: 0;
}

/* A gold spine down the left of an upcoming booking, so the two lists are told apart at a
   glance rather than only by their headings. */
.salon-booking-rail {
  flex-shrink: 0;
  width: 3px;
  background: var(--salon-gold);
  border-radius: 999px;
}

.salon-booking--past .salon-booking-rail {
  background: var(--salon-surface-container-highest);
}

.salon-booking-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.salon-booking-when {
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

.salon-booking-service {
  overflow: hidden;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-surface);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.salon-booking-where {
  overflow: hidden;
  font-size: 12px;
  line-height: 16px;
  color: var(--salon-on-surface-variant);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.salon-booking-tail {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 6px;
  align-items: flex-end;
  justify-content: space-between;
}

.salon-badge {
  padding: 3px 8px;
  font-size: 11px;
  line-height: 14px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
  background: var(--salon-surface-container);
  border-radius: 6px;
}

.salon-badge--live {
  color: #241a00;
  background: rgba(254, 210, 85, 0.25);
}

.salon-badge--off {
  color: var(--salon-error);
  background: rgba(186, 26, 26, 0.08);
}

.salon-booking-total {
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: var(--salon-on-surface);
}

/* ── SH017: vote for stylist ───────────────────────────────────── */
.salon-vote {
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
  padding: 10px 0 0;
  font-family: var(--salon-font-body);
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  text-align: left;
  color: var(--salon-on-surface);
  background: transparent;
  /* A hairline rather than a filled bar: the row belongs to the card, and a second surface
     inside a card that is already lifted off the page reads as a nested card. */
  border: 0;
  border-top: 1px solid var(--salon-border);
  cursor: pointer;
}

.salon-vote:active {
  opacity: 0.6;
}

.salon-vote-star {
  flex-shrink: 0;
  color: var(--salon-gold);
}

.salon-vote-text {
  flex: 1;
}

.salon-vote-chevron {
  flex-shrink: 0;
  color: var(--salon-on-surface-variant);
}

/* The vote already cast. Same strip, no affordance. */
.salon-rated {
  display: flex;
  gap: 3px;
  align-items: center;
  padding: 10px 0 0;
  border-top: 1px solid var(--salon-border);
}

.salon-rated-star {
  flex-shrink: 0;
  color: var(--salon-surface-container-highest);
}

.salon-rated-star--on {
  color: var(--salon-gold);
}

.salon-rated-text {
  margin-left: 6px;
  font-size: 12px;
  line-height: 16px;
  color: var(--salon-on-surface-variant);
}

/* ── Empty & error ─────────────────────────────────────────────── */
.salon-blank {
  padding: 64px 0;
  text-align: center;
}

.salon-blank-title {
  margin: 0 0 8px;
  font-family: var(--salon-font-headline);
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: var(--salon-on-surface);
}

.salon-blank-text {
  max-width: 260px;
  margin: 0 auto;
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
}

.salon-empty {
  padding: 48px 0;
  margin: 0;
  font-size: 14px;
  color: var(--salon-error);
  text-align: center;
}

.salon-retry {
  display: block;
  padding: 8px 20px;
  margin: 12px auto 0;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  color: var(--salon-primary);
  background: transparent;
  border: 1px solid var(--salon-surface-container-highest);
  border-radius: 999px;
  cursor: pointer;
}

/* ── Bottom action ─────────────────────────────────────────────── */
.salon-cta {
  position: fixed;
  bottom: 0;
  left: 50%;
  z-index: 50;
  width: 100%;
  max-width: var(--salon-max-width);
  padding: 16px var(--salon-container-padding)
    calc(20px + env(safe-area-inset-bottom, 0px));
  background: rgba(251, 249, 246, 0.9);
  backdrop-filter: blur(12px);
  transform: translateX(-50%);
}

.salon-cta-btn {
  width: 100%;
  padding: 16px;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-primary);
  background: var(--salon-primary);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.salon-cta-btn:active {
  transform: scale(0.98);
}
</style>
